import { GENERIC_PDF_ERROR } from "./pdfErrorMessage";

// ENG-1865: the sign-all page's copy, kept out of the page so every string is tested.

// A held agreement can't be retried on this page, so the generic "Please try again." would leave the signer nowhere to go.
const HELD_GENERIC = "the document couldn't be prepared. Open your portal and try again.";

export const joinWords = (words) =>
  words.length < 2 ? words.join("") : `${words.slice(0, -1).join(", ")} and ${words[words.length - 1]}`;

export function missingSteps({ unopenedLabels, detailsValid, erroredLabels, hasSignature, consent }) {
  return [
    unopenedLabels.length ? `open ${joinWords(unopenedLabels)}` : null,
    detailsValid ? null : "fill in your details",
    erroredLabels.length ? `resolve the problem shown on ${joinWords(erroredLabels)}` : null,
    hasSignature ? null : "draw your signature",
    consent ? null : "check the box",
  ].filter(Boolean);
}

export const continueText = (missing) => (missing.length ? `To continue, ${joinWords(missing)}.` : "");

export const progressText = (count) => `${count === 1 ? "1 agreement is" : `${count} agreements are`} waiting for your signature`;

export function unavailableText(n) {
  if (!n) return null;
  return n === 1 ? "1 agreement couldn't be loaded and is left out." : `${n} agreements couldn't be loaded and are left out.`;
}

// Why the agreements in these states are left out of the batch: reason is cpBlockedReason's, or "build" with the held PDF's message.
export function blockedText(reason, labels, message) {
  if (!labels.length) return null;
  const states = joinWords(labels);
  if (reason === "missing") {
    return labels.length === 1
      ? `${states} can't be signed yet: your license number isn't on file. Contact support to add it.`
      : `${states} can't be signed yet: your license numbers aren't on file. Contact support to add them.`;
  }
  // Y3: sign-all strips its handoff hash on load, so a reload lands on "There's nothing to sign here" instead of back on this page.
  if (reason === "unavailable") {
    return labels.length === 1
      ? `${states} can't be signed right now: your license number couldn't be loaded. Open your portal and try again, or contact support if it keeps happening.`
      : `${states} can't be signed right now: your license numbers couldn't be loaded. Open your portal and try again, or contact support if it keeps happening.`;
  }
  if (reason === "details-unavailable") {
    return `${states} can't be signed right now: your details couldn't be loaded. Open your portal and try again, or contact support if it keeps happening.`;
  }
  if (reason === "build") {
    return message === GENERIC_PDF_ERROR ? `${states} can't be signed right now: ${HELD_GENERIC}` : `${states} can't be signed: ${message}`;
  }
  return null;
}

// Z1: the reason shown inside a blocked row itself; no state label or plural, since the row already shows its own agreement.
export function blockedRowText(reason, message) {
  if (reason === "missing") return "Can't be signed yet: your license number isn't on file. Contact support to add it.";
  if (reason === "unavailable") {
    return "Can't be signed right now: your license number couldn't be loaded. Open your portal and try again, or contact support if it keeps happening.";
  }
  if (reason === "details-unavailable") {
    return "Can't be signed right now: your details couldn't be loaded. Open your portal and try again, or contact support if it keeps happening.";
  }
  if (reason === "build") return message === GENERIC_PDF_ERROR ? `Can't be signed right now: ${HELD_GENERIC}` : `Can't be signed: ${message}`;
  return null;
}

export const consentText = (labels) =>
  labels.length === 1
    ? `I have reviewed and agree to sign the agreement listed above (${labels[0]}).`
    : `I have reviewed and agree to sign the ${labels.length} agreements listed above (${labels.join(", ")}).`;

export function resultsText(failedCount) {
  if (failedCount === 0) return "You can close this page. Your portal updates when you return to it.";
  return `${failedCount === 1 ? "1 agreement wasn't" : `${failedCount} agreements weren't`} signed. Retry, or sign ${
    failedCount === 1 ? "it" : "them"
  } one at a time from your portal.`;
}

export const successHeading = (count) => (count === 1 ? "Your agreement is signed" : `All ${count} agreements are signed`);

export const signedSummary = (signedCount, count) => `${signedCount} of ${count} signed.`;

export const previewLabel = (label) => `${label} agreement`;

export const signButtonLabel = (count) => (count === 1 ? "Sign" : `Sign all ${count}`);

export function rowStatus(result, opened, buildError) {
  if (result?.status === "signing") return { text: "Signing…", className: "text-gray-600" };
  if (result?.status === "signed") return { text: "Signed", className: "text-green-700" };
  if (result?.status === "failed") return { text: `Failed: ${result.reason}`, className: "text-red-700" };
  if (buildError) return { text: buildError, className: "text-red-700" };
  return opened ? { text: "Opened", className: "text-green-700" } : { text: "Not opened yet", className: "text-amber-700" };
}
